import axios from "axios";
import Api from "../api/axios";
import JSZip from 'jszip';
import { useAccountState } from "../../redux/reducers/accountReducer";
import { CaseDocument } from "../types";
import { useCaseService } from "./useCaseService";
import { useCaseExpenseService } from "./useCaseExpenseService";
import { getMimeTypeFromFileName } from "./fileAttachments";

export const useDocumentDownload = () => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const caseService = useCaseService();
    const expenseService = useCaseExpenseService();

    const downloadAsync = async (caseId: string, docId: string, docName: string) => {
        try {

            // Fetch the SAS URL from your backend API
            const downloadUrl = await Api.get(`/api/CaseDocument/account/${primaryAccount?.account.accountId}/case/${caseId}/doc/${docId}/download`);
            const response = await axios.get(downloadUrl.data, {
                responseType: 'blob' // Ensure the response is treated as a file                
            });

            // Create a blob URL for the downloaded file
            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            // Trigger the download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = docName!;
            document.body.appendChild(link);
            link.click();

            //Clean up
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error("Error fetching SAS URL:", error);
        }
    };

    const downloadManyExpensesAsZipAsync = async (caseId: string, attachmentIds: string[]) => {
        const zip = new JSZip();

        const caseObj = caseService.findCaseById(caseId);
        const expenses = expenseService.expenses.filter(exp => exp.caseId === caseId && attachmentIds.includes(exp.expenseId as string));

        const results = await Promise.all(
            expenses.map(async (exp) => {
                const sas = await Api.get(
                    `/api/CaseDocument/account/${primaryAccount?.account.accountId}/case/${caseObj?.caseId}/doc/${exp.attachmentId}/download`,
                );
                const response = await axios.get<Blob>(sas.data, { responseType: 'blob' });
                return { name: exp.attachmentName as string, blob: response.data };
            }),
        );

        for (const { name, blob } of results) {
            zip.file(name, blob);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const blobUrl = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `expenses_${caseObj?.caseId}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
    };

    const downloadDocAsync = async (doc: CaseDocument) => {
        try {

            await downloadAsync(doc.caseId as string, doc.docId as string, doc.name as string);

        } catch (error) {
            console.error("Error fetching SAS URL:", error);
        }
    }

    /**
     * Fetch a document's contents as a File. Unlike downloadDocAsync this throws on
     * failure so callers can surface the error instead of silently doing nothing.
     */
    const getDocumentFileAsync = async (doc: CaseDocument): Promise<File> => {
        const sas = await Api.get(
            `/api/CaseDocument/account/${primaryAccount?.account.accountId}/case/${doc.caseId}/doc/${doc.docId}/download`,
        );
        const response = await axios.get<Blob>(sas.data, { responseType: 'blob' });
        const fileName = doc.name as string;

        return new File([response.data], fileName, { type: getMimeTypeFromFileName(fileName) });
    }
   
    return {
        downloadAsync,
        downloadDocAsync,
        downloadManyExpensesAsZipAsync,
        getDocumentFileAsync,
    };
}